import React, { useState } from "react";
import ErrorRow from "Common/ErrorRow";
import BreadCrumb from "Common/BreadCrumb";
import { Landmark, Search, RefreshCw, CheckCircle, XCircle } from "lucide-react";
import { ToastContainer } from "react-toastify";
import useAgencies from "hooks/useAgencies";
import { Agency } from "services/agency.service";

const Agencies = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const { data: response, isLoading, isError, refetch } = useAgencies({
    page,
    limit: 20,
    search: debouncedSearch || undefined,
  });

  const agencies: Agency[] = response?.data?.data ?? [];
  const total = response?.data?.total ?? 0;
  const totalPages = Math.ceil(total / 20);

  const handleSearch = (val: string) => {
    setSearch(val);
    clearTimeout((handleSearch as any)._t);
    (handleSearch as any)._t = setTimeout(() => {
      setDebouncedSearch(val);
      setPage(1);
    }, 400);
  };

  return (
    <React.Fragment>
      <BreadCrumb title="Agencies" pageTitle="Users" />
      <ToastContainer closeButton={false} limit={1} />

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {/* Toolbar */}
        <div className="px-5 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="relative flex-1 sm:max-w-xs">
            <Search className="absolute left-3 top-2.5 size-4 text-gray-400" />
            <input
              type="text"
              placeholder="Filter this page by agency name or email..."
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-9 w-full px-3 py-2 text-sm rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent placeholder:text-gray-400"
            />
          </div>
          <button
            onClick={() => refetch()}
            className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="size-4" /> Refresh
          </button>
          <span className="sm:ml-auto text-sm text-gray-500">{total} total</span>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table data-stack className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Agency</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Contact</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Location</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Verified</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Owner</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Joined</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isError ? (
                <ErrorRow colSpan={7} label="Couldn't load agencies" onRetry={refetch} />
              ) : isLoading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-400">
                    Loading...
                  </td>
                </tr>
              ) : agencies.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-16 text-center">
                    <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Landmark className="size-6 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-500">No agencies found</p>
                  </td>
                </tr>
              ) : (
                agencies.map((agency) => (
                  <tr key={agency._id} className="hover:bg-gray-50 transition-colors">
                    <td data-label="Agency" data-cell="title" className="px-4 py-3">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        {agency.profilePicture?.url ? (
                          <img
                            src={agency.profilePicture.url}
                            alt=""
                            className="size-8 rounded-full object-cover flex-shrink-0"
                          />
                        ) : (
                          <div className="size-8 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 font-semibold text-sm flex-shrink-0">
                            {(agency.name || agency.user?.firstName)?.charAt(0)?.toUpperCase()}
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-gray-900">
                            {agency.name || `${agency.user?.firstName} ${agency.user?.lastName}`}
                          </p>
                          {agency.cacNumber && (
                            <p className="text-xs text-gray-400">CAC: {agency.cacNumber}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td data-label="Contact" data-cell="block" className="px-4 py-3">
                      <p className="text-gray-700">{agency.companyEmail || agency.user?.email}</p>
                      <p className="text-xs text-gray-400">{agency.phoneNumber || agency.user?.phoneNumber}</p>
                    </td>
                    <td data-label="Location" className="px-4 py-3 text-gray-600">
                      {[agency.lga, agency.state].filter(Boolean).join(", ") || "—"}
                    </td>
                    <td data-label="Type" className="px-4 py-3 text-gray-600 capitalize">
                      {agency.type || "—"}
                    </td>
                    <td data-label="Verified" className="px-4 py-3">
                      {agency.agencyVerified ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle className="size-3" /> Verified
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                          <XCircle className="size-3" /> Unverified
                        </span>
                      )}
                    </td>
                    <td data-label="Owner" className="px-4 py-3 text-gray-600">
                      {agency.user?.firstName} {agency.user?.lastName}
                    </td>
                    <td data-label="Joined" className="px-4 py-3 text-gray-500 whitespace-nowrap">
                      {new Date(agency.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Page {page} of {totalPages} · {total} total
            </p>
            <div className="flex gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition-colors"
              >
                Previous
              </button>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

export default Agencies;
